import { call, all, put, takeLatest, select } from 'redux-saga/effects';

import { formatMessage } from '~/i18n/translate';

import { ISA_ERROR, REASON_TYPE, USER_ROLE } from '~/constants';
import { GET_ALL_REQUEST, GET_BY_ID_REQUEST, SAVE_REQUEST, EDIT_ITEM, DELETE_ITEM } from './reason-constants';

import * as reasonActions from './reason-actions';

import history from '~/services/history';
import reasonService from './reason-service';
import notification from '~/services/notification';
import userService from '~/services/modules/user/user-service';
import domainService from '~/services/modules/domain/domain-service';

import { sort } from '../order/order-utils';
import { AppStateType } from '../root-reducer';

const INITIAL_VALUES = {
  code: '',
  name: '',
  type: REASON_TYPE.CLIENTE_BLOCK,
  actions: {
    bonifications: 'allow',
    orders: 'allow',
    visits: 'allow'
  },
  description: '',
  subsidiaries: [],
  externalCode: '',
  vendors: [],
  allowToken: false
};

export function* getAll() {
  try {
    yield put(reasonActions.setLoadingData(true));

    const { data } = yield call(reasonService.getAll);
    if (data) {
      yield put(
        reasonActions.getAllSuccess([
          ...data.map(item => ({
            ...item,
            typeParse: reasonService.parseType(item.type)
          }))
        ])
      );
    }

    yield put(reasonActions.setLoadingData(false));
  } catch (error) {
    yield put(reasonActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getById({ id }: any) {
  try {
    yield put(reasonActions.setLoadingData(true));

    const user = yield select((state: AppStateType) => state.auth.user);

    const [{ data: subsidiaries }, { data: users }] = yield all([
      call(domainService.getSubsidiaries, { userSubsidiaryFilter: user?.userSubsidiaries ?? [] }),
      call(userService.getUsers)
    ]);

    if (!id) {
      yield put(
        reasonActions.getByIdSuccess({
          data: INITIAL_VALUES,
          selects: {
            subsidiaries: sort(subsidiaries),
            vendors: sort(users?.filter(seller => seller?.userRole === USER_ROLE?.VENDOR))
          }
        })
      );
    } else {
      const { data } = yield call(reasonService.getReason, id);

      let parsedSubsidiaries = [];

      if (data?.subsidiaryExternalCode) {
        parsedSubsidiaries = [
          ...parsedSubsidiaries,
          ...subsidiaries?.filter(item => item?.externalCode === data?.subsidiaryExternalCode)
        ];
      }

      if (data?.subsidiariesExternalCodes) {
        parsedSubsidiaries = [
          ...parsedSubsidiaries,
          ...subsidiaries?.filter(item => data?.subsidiariesExternalCodes?.includes(item?.externalCode))
        ];
      }

      const parsedData = {
        ...data,
        subsidiaries: parsedSubsidiaries,
        token: data?.token?.password,
        vendors: data?.token?.userExternalCode?.map((externalCode: string) =>
          users?.find(user => user?.externalCode === externalCode)
        ),
        allowToken: data?.token?.password ? true : false
      };

      yield put(
        reasonActions.getByIdSuccess({
          data: parsedData,
          selects: {
            subsidiaries: sort(subsidiaries),
            vendors: sort(users?.filter(seller => seller?.userRole === USER_ROLE?.VENDOR))
          }
        })
      );
    }
    yield put(reasonActions.setLoadingData(false));
  } catch (error) {
    yield put(reasonActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* save(data: any) {
  try {
    const { id, payload } = data;
    yield put(reasonActions.setLoadingSend(true));

    const daysToExpiryPassword = new Date();
    daysToExpiryPassword.setDate(daysToExpiryPassword.getDate() + 1);

    const team = (({ subsidiaries, vendors, allowToken, users, token, ...rest }) => ({
      ...rest,
      usersIDs: users?.map((s: any) => s?._id),
      subsidiariesExternalCodes: subsidiaries?.map(subsidiary => subsidiary?.externalCode),
      token:
        token && allowToken
          ? {
              password: token,
              passwordExpirationDate: daysToExpiryPassword.toISOString().slice(0, 10) + 'T23:59:59',
              active: token ? true : false,
              userExternalCode: vendors?.map(user => user?.externalCode)
            }
          : undefined
    }))(payload);

    if (id) {
      yield call(reasonService.editReason, { _id: id, ...team });
    } else {
      yield call(reasonService.createReason, team);
    }
    yield call(history.push, `/register/reason/`, {
      date: new Date().getTime()
    });
    yield put(reasonActions.setLoadingSend(false));
    yield call(
      notification.success,
      id ? formatMessage('reasonFormUpdatedSuccess') : formatMessage('reasonFormCreatedSuccess')
    );
  } catch (error) {
    yield put(reasonActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('commonLabelExcludeItem')
    );
    if (isConfirmed) {
      yield put(reasonActions.setLoadingSend(true));
      yield call(reasonService.deleteReason, id);
      yield put(reasonActions.setLoadingSend(false));

      yield put(reasonActions.getAllRequest());
      yield call(notification.success, formatMessage('reasonFormExcludeSuccess'));
    }
  } catch (error) {
    yield put(reasonActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/register/reason/edit/${id}`);
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_REQUEST, save),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(EDIT_ITEM, editItem)
]);
